/* SupplierSwitcher — top-right dropdown in the buyer portal. Shows the
   current active supplier (or "All suppliers") + list of connected tenants +
   any pending invites with inline-accept + link to discover apps. */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSupplier } from "../context/SupplierContext";
import { useSellerProfileDrawer } from "../context/SellerProfileDrawerContext";
import {
  ChevronDown, Check, Store, Mail, Sparkles, X, Home, Eye, Info,
} from "lucide-react";
import { toast } from "sonner";

export default function SupplierSwitcher() {
  const { connections, invites, active, selectSupplier, clearSupplier, acceptInvite } = useSupplier();
  const { open: openProfile } = useSellerProfileDrawer();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const onAccept = async (token) => {
    try {
      const d = await acceptInvite(token);
      toast.success(`Connected to ${d.tenant_name}`);
      setOpen(false);
      nav("/buyer");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const branding = active?.branding || {};
  const chipStyle = active ? {
    background: branding.primary_color || "#0a1f1c",
    color: branding.accent_color || "#e8ff3a",
  } : {};

  return (
    <div className="relative" ref={ref} data-testid="supplier-switcher">
      <div
        className="inline-flex items-stretch rounded-full border border-foreground/15 bg-white text-[13px] transition-all overflow-hidden"
        style={active ? { borderColor: (branding.accent_color || "#e8ff3a") + "80" } : {}}
      >
        <button
          onClick={() => setOpen(v => !v)}
          data-testid="supplier-switcher-btn"
          className="inline-flex items-center gap-2 px-3 h-9 hover:bg-secondary/40 transition-colors"
        >
          {active ? (
            <>
              <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold display shrink-0" style={chipStyle}>
                {branding.logo_url
                  ? <img src={branding.logo_url} alt="" className="w-full h-full object-contain"/>
                  : active.tenant_name.slice(0,1)}
              </span>
              <span className="max-w-[140px] truncate">{active.tenant_name}</span>
            </>
          ) : (
            <>
              <Home className="w-3.5 h-3.5 text-foreground/60"/>
              <span>verqo · All suppliers</span>
            </>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-foreground/40 transition-transform ${open ? "rotate-180" : ""}`}/>
          {invites.length > 0 && (
            <span
              data-testid="supplier-switcher-invite-badge"
              className="ml-1 relative inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold ring-2 ring-white"
              title={`${invites.length} pending invite${invites.length > 1 ? "s" : ""}`}
            >
              <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-60"/>
              <span className="relative">{invites.length}</span>
            </span>
          )}
        </button>
        {/* Inline info-icon — opens the seller-profile drawer without toggling the dropdown.
            Only visible in storefront mode (when an active supplier is set). */}
        {active && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openProfile(active.tenant_slug); }}
            data-testid="supplier-switcher-profile-btn"
            title={`View ${active.tenant_name}'s profile`}
            aria-label="View seller profile"
            className="px-2.5 grid place-items-center text-foreground/45 hover:text-foreground hover:bg-foreground/5 border-l border-foreground/10 transition-colors"
          >
            <Info className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>

      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-2xl border border-foreground/10 bg-white shadow-xl z-50 overflow-hidden" data-testid="supplier-switcher-menu">
          {/* Hub option */}
          <button
            onClick={() => { clearSupplier(); setOpen(false); nav("/buyer"); }}
            data-testid="switcher-hub"
            className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-secondary/40 transition-colors ${!active ? "bg-secondary/30" : ""}`}
          >
            <span className="w-8 h-8 rounded-lg bg-foreground grid place-items-center text-background shrink-0">
              <Home className="w-4 h-4"/>
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-medium">verqo hub</div>
              <div className="text-[11.5px] text-foreground/55">Unified view across every supplier</div>
            </div>
            {!active && <Check className="w-4 h-4 text-foreground"/>}
          </button>

          {/* Connected suppliers */}
          {connections.length > 0 && (
            <div className="border-t border-foreground/5">
              <div className="px-4 pt-3 pb-1 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50">Connected suppliers</div>
              <div className="max-h-64 overflow-y-auto">
                {connections.map(c => {
                  const b = c.branding || {};
                  const isOn = active?.tenant_slug === c.tenant_slug;
                  return (
                    <div
                      key={c.id}
                      className={`group flex items-stretch hover:bg-secondary/40 transition-colors ${isOn ? "bg-secondary/30" : ""}`}
                      data-testid={`switcher-supplier-${c.tenant_slug}`}
                    >
                      <button
                        onClick={() => { selectSupplier(c); setOpen(false); nav("/buyer"); }}
                        className="flex-1 text-left px-4 py-2.5 flex items-center gap-3 min-w-0"
                      >
                        <span
                          className="w-8 h-8 rounded-lg grid place-items-center text-[11px] font-bold display shrink-0"
                          style={{ background: b.primary_color || "#0a1f1c", color: b.accent_color || "#e8ff3a" }}
                        >
                          {b.logo_url
                            ? <img src={b.logo_url} alt="" className="w-full h-full object-contain p-1"/>
                            : c.tenant_name.slice(0,1)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13.5px] font-medium truncate">{c.tenant_name}</div>
                          <div className="text-[11px] text-foreground/55">
                            {(c.default_terms || "NET_30").replace("NET_", "Net-")}
                            {(c.additional_credit_limit || 0) > 0 && ` · +$${c.additional_credit_limit.toLocaleString()}`}
                          </div>
                        </div>
                        {isOn && <Check className="w-4 h-4 text-foreground shrink-0"/>}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpen(false); openProfile(c.tenant_slug); }}
                        title="View seller profile"
                        data-testid={`switcher-view-profile-${c.tenant_slug}`}
                        className="px-3 grid place-items-center text-foreground/45 hover:text-foreground hover:bg-foreground/5 border-l border-foreground/5 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pending invites */}
          {invites.length > 0 && (
            <div className="border-t border-foreground/5">
              <div className="px-4 pt-3 pb-1 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50 flex items-center gap-1.5">
                <Mail className="w-3 h-3"/>Pending invites ({invites.length})
              </div>
              {invites.map(inv => (
                <div key={inv.id} className="px-4 py-2.5 border-t border-foreground/5 first:border-t-0" data-testid={`switcher-invite-${inv.id}`}>
                  <div className="text-[13px] font-medium">{inv.tenant_name}</div>
                  <div className="text-[11px] text-foreground/55 mt-0.5">
                    {(inv.default_terms || "NET_30").replace("NET_", "Net-")}
                    {(inv.additional_credit_limit || 0) > 0 && ` · +$${inv.additional_credit_limit.toLocaleString()}`}
                  </div>
                  <button
                    onClick={() => onAccept(inv.token)}
                    data-testid={`switcher-accept-${inv.id}`}
                    className="mt-1.5 text-[12px] font-medium text-foreground hover:underline"
                  >
                    Accept →
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Footer — discover */}
          <div className="border-t border-foreground/5 px-4 py-2.5">
            <button
              onClick={() => { setOpen(false); nav("/buyer/apps"); }}
              data-testid="switcher-discover"
              className="w-full text-left flex items-center gap-2 text-[12.5px] text-foreground/65 hover:text-foreground"
            >
              <Sparkles className="w-3.5 h-3.5 text-accent"/>
              Discover more suppliers
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
